import Media from '@components/ui/Media/Media'
import {
  CSSProperties,
  FC,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react'

import useMediaQuery from '@lib/hooks/useMediaQuery'
import { generateMediaUrlV2 } from '@lib/utils'
import { isMobile } from '@utils/helpers/DeviceUtils'
import clsx from 'clsx'
import 'photoswipe/dist/photoswipe.css'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import SwiperClass from 'swiper'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/thumbs'
import { Swiper, SwiperSlide } from 'swiper/react'
import productStyles from '../Product.module.scss'

import { ProductDetailVariantFragment } from '@lib/graphql/graphql'
import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { isContentImage } from '../helpersv2'
import s from './ProductImages.module.scss'
export type ProductImageType = {
  src: string
  fileName?: string
  hash?: string
}

export interface ProductImagesProps {
  images: ProductImageType[]
  padding?: boolean
  className?: string
  ref?: any
  selectedVariant: ProductDetailVariantFragment
}
const ProductImages: FC<ProductImagesProps> = forwardRef(
  ({ images, padding, className, selectedVariant }, ref) => {
    const [swiper, setSwiper] = useState<SwiperClass>(null)
    const [isZoomed, setIsZoomed] = useState(false)

    const progressLineRef = useRef<HTMLDivElement>(null)
    const [activeIndex, setActiveIndex] = useState(0)
    const isBreakingPoint = useMediaQuery('(max-width: 834px)')
    const onSlideChange = (e: SwiperClass) => {
      const transparent = isContentImage(images[e.realIndex].fileName)
      const productMain = document.querySelector(
        `.${productStyles['product-main']}`,
      ) as HTMLElement
      if (transparent && productMain && isBreakingPoint) {
        progressLineRef.current?.classList.add(s['with-content-image'])
        productMain?.style.setProperty('--color', 'rgb(255, 255, 255)')
      } else {
        progressLineRef.current?.classList.remove(s['with-content-image'])
        productMain?.style.setProperty('--color', 'rgb(0, 0, 0)')
      }
      setActiveIndex(e.realIndex)
    }

    const mobileImagesWithIndex = images
      .filter((image) => !image.fileName.includes('_V_'))
      .map((image) => {
        const index = images.findIndex((img) => img.fileName === image.fileName)
        return { ...image, index }
      })

    useEffect(() => {
      if (swiper) swiper.slideTo(0)

      if (!isMobile()) return

      const lightbox = new PhotoSwipeLightbox({
        pswpModule: () => import('photoswipe'),
        children: `.${s.media} img`,
        gallery: '#gallery',
        thumbSelector: `.${s.media}`,
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 1.6,
        maxZoomLevel: 1,
        counter: false,
        zoom: false,
        preloaderDelay: 0,
        bgOpacity: 1,
        showHideAnimationType: 'zoom',
        loop: false,
        arrowPrev: false,
        arrowNext: false,
        close: false,
        mainClass: s.pswp,
      })

      lightbox.addFilter('domItemData', (el: any) => {
        const { element } = el

        return {
          src: element.src,
          w: element.naturalWidth * 2,
          h: element.naturalHeight * 2,
          msrc: element.src,
          thumbCropped: true,
        }
      })

      lightbox.on('uiRegister', () => {
        lightbox.pswp.ui.registerElement({
          name: 'button-group',
          isButton: false,
          tagName: 'div',
          appendTo: 'root',
          order: 0,

          onInit: (el) => {
            el.classList.add(s['pswp-button-group'])

            const hiddenBtnGroup = document
              .querySelector('.hidden-btn-group')
              .cloneNode(true)

            hiddenBtnGroup.childNodes.forEach((node: any) => {
              const btn = node.cloneNode(true)

              if (btn.className.includes(s['prev-btn'])) {
                btn.onclick = () => lightbox.pswp.prev()
              }

              if (btn.className.includes(s['close-btn'])) {
                btn.onclick = () => lightbox.pswp.close()
              }

              if (btn.className.includes(s['next-btn'])) {
                btn.onclick = () => lightbox.pswp.next()
              }

              el.appendChild(btn)
            })
          },
        })
      })

      //lightbox.on next and prev
      lightbox.on('change', () => {
        const currentImage = mobileImagesWithIndex.find((image) =>
          lightbox.pswp.currSlide.data.src.includes(image.hash),
        )

        if (swiper && currentImage) swiper.slideTo(currentImage.index)

        if (lightbox.pswp.currIndex === 0) {
          document
            .querySelector(`.${s.pswp} .${s['prev-btn']}`)
            .classList.add(s.disabled)
        } else {
          document
            .querySelector(`.${s.pswp} .${s['prev-btn']}`)
            .classList.remove(s.disabled)
        }

        if (lightbox.pswp.currIndex === mobileImagesWithIndex.length - 1) {
          document
            .querySelector(`.${s.pswp} .${s['next-btn']}`)
            .classList.add(s.disabled)
        } else {
          document
            .querySelector(`.${s.pswp} .${s['next-btn']}`)
            .classList.remove(s.disabled)
        }
      })

      lightbox.init()

      return () => {
        lightbox.destroy()
      }
    }, [images, swiper])

    const discountedPrice = selectedVariant?.price?.discountedPrice
    return (
      <div
        className={clsx(s['container'], className)}
        style={
          {
            '--image-count': images.length,
            '--active-index': activeIndex,
            '--content-image': isContentImage(
              images.length > activeIndex && images[activeIndex].fileName,
            )?.toString(),
          } as CSSProperties
        }
        ref={ref as any}
      >
        {/* {discountedPrice &&
          discountedPrice?.applications[0].value.__typename ===
            'RelativeDiscountApplicationValue' && (
            <div className={s.discountTag}>
              <ProductCartTag
                fill="var(--discount-red)"
                label={`${discountedPrice.applications[0].value.factor * 100}%`}
                labelColor="rgb(255, 255, 255)"
                height={'32px'}
              />
            </div>
          )} */}

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            swiper.slidePrev()
          }}
          disabled={activeIndex === 0 || images.length === 1}
          className={clsx(
            s['navigation-btn-wrapper'],
            s['slide-previous-btn-wrapper'],
            { [s['btn-hidden']]: images.length === 0 || isZoomed },
          )}
        >
          <span className={clsx(s['navigation-btn'], s['slide-previous-btn'])}>
            <svg
              width="14"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.89453 10.991L0.999028 6.09546L5.89453 1.19996"
                stroke="black"
              />
              <line
                x1="1.30469"
                y1="6.09998"
                x2="13.5434"
                y2="6.09998"
                stroke="black"
              />
            </svg>
          </span>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            swiper.slideNext()
          }}
          disabled={activeIndex === images.length - 1 || images.length === 1}
          className={clsx(
            s['navigation-btn-wrapper'],
            s['slide-next-btn-wrapper'],
            { [s['btn-hidden']]: images.length === 0 || isZoomed },
          )}
        >
          <span className={clsx(s['navigation-btn'], s['slide-next-btn'])}>
            <svg
              width="14"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.89453 10.991L0.999028 6.09546L5.89453 1.19996"
                stroke="black"
              />
              <line
                x1="1.30469"
                y1="6.09998"
                x2="13.5434"
                y2="6.09998"
                stroke="black"
              />
            </svg>
          </span>
        </button>

        <div className="hidden-btn-group hidden">
          <button
            className={clsx(s['navigation-btn'], s['prev-btn'], s.disabled)}
          >
            <svg
              width="14"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.89453 10.991L0.999028 6.09546L5.89453 1.19996"
                stroke="black"
              />
              <line
                x1="1.30469"
                y1="6.09998"
                x2="13.5434"
                y2="6.09998"
                stroke="black"
              />
            </svg>
          </button>
          <button className={clsx(s['navigation-btn'], s['close-btn'])}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 1L13 13M13 1L1 13" stroke="black" strokeWidth="1" />
            </svg>
          </button>
          <button
            className={clsx(
              s['navigation-btn'],
              s['next-btn'],
              s['slide-next-btn'],
            )}
          >
            <svg
              width="14"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.89453 10.991L0.999028 6.09546L5.89453 1.19996"
                stroke="black"
              />
              <line
                x1="1.30469"
                y1="6.09998"
                x2="13.5434"
                y2="6.09998"
                stroke="black"
              />
            </svg>
          </button>
        </div>

        <Swiper
          id="gallery"
          slidesPerView={1}
          onSwiper={setSwiper}
          init={false}
          grabCursor
          onSlideChange={onSlideChange}
          className={s['swiper']}
          watchOverflow={true}
        >
          {images.map((image, index) => {
            return (
              <SwiperSlide key={index}>
                <Media
                  onZoom={(zoom) => {
                    // disable swiper buttons when zoomed
                    if (zoom) {
                      swiper.disable()
                      setIsZoomed(true)
                    } else {
                      swiper.enable()
                      setIsZoomed(false)
                    }
                  }}
                  className={s['media']}
                  mediaClassName={clsx(s['image'], {
                    [s['content-image']]: isContentImage(image.fileName),
                    [s['padding']]: padding,
                  })}
                  sizes="(min-width: 834px) 40vw, 100vw"
                  priority
                  // priority={index === 0}
                  mediaWithCrops={
                    {
                      __typename: 'MediaWithCrops',
                      url: generateMediaUrlV2(image.src),
                      media: {
                        __typename: 'Image',
                        url: generateMediaUrlV2(image.src),
                      },
                    } as MediaWithCropsFragment
                  }
                  disableShopTheLook
                  clickToZoom={!isMobile()}
                />
              </SwiperSlide>
            )
          })}
        </Swiper>

        <div
          className={clsx(s['image-progress-container'], {
            [s['image-progress-show']]: images.length > 1,
          })}
          ref={progressLineRef}
        >
          <>
            <span className={s['counter']}>
              {activeIndex + 1} / {images.length}
            </span>
            <span className={s['progress-line']}>
              <span className={s['progress-bar']} />
            </span>
          </>
        </div>
      </div>
    )
  },
)

ProductImages.displayName = 'ProductImages'
export default ProductImages
